import { MapElementFactory } from 'vue2-google-maps'

export function handleGetNonClusteredDataSet(map, dataset, bounds, clusters, clusterOptions) {
  const clusteredMarkers = clusters
    .map(
      ({ markers_: markers }) =>
        markers.length >= clusterOptions.minimumClusterSize && markers.map(marker => marker.title)
    )
    .filter(Boolean)
    .flat()

  const visibleMarkers = dataset
    .map(item => {
      const marker = new map.Marker({
        position: {
          lat: item.coordinates.latitude,
          lng: item.coordinates.longitude
        }
      })

      return bounds.contains(marker.getPosition()) && item
    })
    .filter(Boolean)

  return visibleMarkers.filter(item => !clusteredMarkers.includes(item.code))
}

export function getTravelModeDurationMethodName(travelMode) {
  const travelModesMapping = {
    WALKING: 'walkingTime',
    DRIVING: 'drivingTime',
    TRANSIT: 'transitTime'
  }

  return travelModesMapping[travelMode]
}

export const clusterImage = [
  {
    url: `data:image/svg+xml;base64, ${window.btoa(
      '<svg width="44" height="56" viewBox="0 0 44 56" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M42 22.0669C42 38.1204 22 53.1706 22 53.1706C22 53.1706 2 38.1204 2 22.0669C2 10.9843 10.9543 2 22 2C33.0457 2 42 10.9843 42 22.0669Z" fill="#D60812" stroke="white" stroke-width="3"/></svg>'
    )}`,
    width: 44,
    height: 56
  }
]

export const clusterCircleImage = [
  {
    url: `data:image/svg+xml;base64, ${window.btoa(
      '<svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="22" cy="22" r="22" fill="#29343D"/></svg>'
    )}`,
    width: 44,
    height: 44,
    textColor: 'white'
  }
]

export default MapElementFactory({
  name: 'directionsRenderer',
  ctr() {
    return window.google.maps.DirectionsRenderer
  },
  events: ['directions_changed', 'onRouteDuration'],
  mappedProps: {},
  props: {
    origin: { type: [Object, Array] },
    destination: { type: [Object, Array] },
    waypoints: { type: Array },
    currentTravelModeSelected: { type: String },
    unitSystem: { type: Number },
    optimizeWaypoints: { type: Boolean },
    resetRoutes: { type: Boolean },
    refreshRoutes: { type: Boolean },
    directionLineOptions: { type: Object }
  },
  afterCreate(directionsRenderer) {
    const directionsService = new window.google.maps.DirectionsService()
    const travelModesObject = window.google.maps.TravelMode
    const travelModesArray = [
      travelModesObject.DRIVING,
      travelModesObject.WALKING,
      travelModesObject.TRANSIT
    ]

    this.$watch(
      () => [this.resetRoutes],
      (value, oldValue) => {
        if (value !== oldValue) {
          directionsRenderer.setDirections({ routes: [] })
        }
      }
    )

    this.$watch(
      () => [this.refreshRoutes],
      (value, oldValue) => {
        if (value !== oldValue) {
          directionsRenderer.setDirections(this.routes[this.currentTravelModeSelected])
        }
      }
    )

    this.$watch(
      () => [
        this.origin,
        this.destination,
        this.currentTravelModeSelected,
        this.unitSystem,
        this.waypoints
      ],
      (value, oldValue) => {
        const {
          origin,
          destination,
          currentTravelModeSelected,
          unitSystem,
          waypoints,
          optimizeWaypoints
        } = this
        let triggerCall = false

        const shouldForceCall =
          this.waypoints &&
          oldValue[2] === travelModesObject.TRANSIT &&
          value[2] === travelModesObject.DRIVING

        if (shouldForceCall) {
          triggerCall = true
        }

        if (!origin || !destination) {
          directionsRenderer.setDirections({ routes: [] })
          return
        }

        const isOriginChanged = value[0] && value[0] !== oldValue[0]
        const isDestinationChanged = value[1] && value[1] !== oldValue[1]
        const hasDeletedWaypoints = !value[4] && oldValue[4]
        const waypointArrayHasChanged = value[4] && !arraysEqual(value[4], oldValue[4])
        const areWaypointsChanged = hasDeletedWaypoints || waypointArrayHasChanged
        const shouldTriggerCall =
          triggerCall || isOriginChanged || isDestinationChanged || areWaypointsChanged

        const hasTransitDuration = Object.values(this.transitTime).filter(Boolean).length

        if (this.waypoints && hasTransitDuration) {
          const noTravelTime = { hours: 0, minutes: 0 }

          this.routes.TRANSIT = {}

          this[getTravelModeDurationMethodName('TRANSIT')] = noTravelTime
          this.handleDurationChange('TRANSIT', noTravelTime)
        }

        if (this.waypoints && currentTravelModeSelected === travelModesArray[2]) {
          const noTravelTime = { hours: 0, minutes: 0 }

          this.routes.TRANSIT = {}
          this[getTravelModeDurationMethodName('TRANSIT')] = noTravelTime
          this.handleDurationChange('TRANSIT', noTravelTime)
          return
        }

        const parsedTravelModeArray = [
          ...(this.waypoints
            ? travelModesArray.filter((_, index) => index < travelModesArray.length - 1)
            : travelModesArray)
        ]

        parsedTravelModeArray.forEach((travelMode, index) => {
          if (
            !shouldTriggerCall &&
            currentTravelModeSelected === travelMode &&
            this.routes[travelMode]
          ) {
            directionsRenderer.setDirections(this.routes[travelMode])
            this.handleDurationChange(travelMode, this[getTravelModeDurationMethodName(travelMode)])
            return
          }

          if (shouldTriggerCall) {
            this.triggerCall(
              directionsService,
              directionsRenderer,
              this.directionLineOptions,
              { origin, destination, travelMode, unitSystem, waypoints, optimizeWaypoints },
              parsedTravelModeArray,
              index
            )

            triggerCall = false
          }
        })
      }
    )
  },
  data: () => {
    return {
      routes: {},
      walkingTime: {},
      transitTime: {},
      drivingTime: {},
      savedOrigin: {},
      savedDestination: {},
      savedWaypoints: {},
      directionsService: null,
      directionsRenderer: null
    }
  },
  methods: {
    capitalize(string) {
      return string.charAt(0).toUpperCase() + string.toLowerCase().slice(1)
    },
    handleWalkingTimeDurationChange(timeDuration) {
      this.$emit('onWalkingTimeDurationChange', timeDuration)
    },
    handleDrivingTimeDurationChange(timeDuration) {
      this.$emit('onDrivingTimeDurationChange', timeDuration)
    },
    handleTransitTimeDurationChange(timeDuration) {
      this.$emit('onTransitTimeDurationChange', timeDuration)
    },
    handleDurationChange(travelMode, timeDuration) {
      this[`handle${this.capitalize(travelMode)}TimeDurationChange`](timeDuration)
    },

    triggerCall(
      directionsService,
      directionsRenderer,
      directionLineOptions,
      { origin, destination, travelMode, unitSystem, waypoints, optimizeWaypoints }
    ) {
      directionsService.route(
        { origin, destination, travelMode, unitSystem, waypoints, optimizeWaypoints },
        (response, status) => {
          if (status !== 'OK') {
            const noTravelTime = { hours: 0, minutes: 0 }

            this[getTravelModeDurationMethodName(travelMode)] = noTravelTime
            this.handleDurationChange(travelMode, noTravelTime)
            return
          }

          const totalTime = response.routes[0].legs
            .map(leg => leg.duration.value)
            .reduce((sum, value) => sum + value, 0)

          const maxTotalTimePermitted = {
            WALKING: 100000,
            TRANSIT: 50000
          }

          if (maxTotalTimePermitted[travelMode] && totalTime > maxTotalTimePermitted[travelMode]) {
            const noTravelTime = { hours: 0, minutes: 0 }

            this[getTravelModeDurationMethodName(travelMode)] = noTravelTime
            this.handleDurationChange(travelMode, noTravelTime)
            return
          }

          directionsRenderer.setOptions({
            polylineOptions: directionLineOptions,
            suppressMarkers: true
          })

          if (this.currentTravelModeSelected === travelMode) {
            directionsRenderer.setDirections(response)
          }

          this.routes[travelMode] = response

          const travelModeDuration = {
            hours: calculateHours(totalTime),
            minutes: calculateMinutes(totalTime)
          }

          this[getTravelModeDurationMethodName(travelMode)] = travelModeDuration
          this.handleDurationChange(travelMode, travelModeDuration)
        }
      )
    }
  }
})

export function calculateHours(time) {
  const timeInHours = time / 3600
  return Math.trunc(timeInHours)
}

export function calculateMinutes(time) {
  const timeInHours = time / 3600
  return Math.trunc((timeInHours % 1) * 60)
}

export function objectsEqual(objectOne, objectTwo) {
  if (!objectOne || !objectTwo) {
    return false
  }

  return typeof objectOne === 'object' && Object.keys(objectOne).length > 0
    ? Object.keys(objectOne).length === Object.keys(objectTwo).length &&
        Object.keys(objectOne).every(p => objectsEqual(objectOne[p], objectTwo[p]))
    : objectOne === objectTwo
}

const arraysEqual = (a1, a2) => {
  if (!a1 || !a2) {
    return
  }
  return a1.length === a2.length && a1.every((o, idx) => objectsEqual(o, a2[idx]))
}
